import { ApplicationElement, html } from '@crossiety/lit-components';
import '@crossiety/lit-components/components/label/element.js';
import '../time_range/element.js';
import '../date_widget/element.js';
import style from './style.lit.css';
import translations from './translations.js';

/**
 * Event card element - links to a card with more details. Event name/text is inserted via default slot.
 * Used for Event card List view.
 *
 * @prop startDate - For card's xs-date-widget and date bellow title.
 * @prop endDate - For calculating finalDate. Final date is displayed date if current date is between startDate and endDate.
 * @prop url - URL for card details. (optional)
 * @prop allDay - Does the event last a full day? If true, we won't show time. (optional)
 * @prop label - Card's label in right-bottom corner. (optional)
 *
 * @slot default - For title.
 * @slot footer - For Origin and Publisher
 *
 * @cssprop --height - Component height.
 * @cssprop --date-widget-bg - Date widget background.
 * @cssprop --date-widget-text - Date widget text color.
 */

export default class Card_V2 extends ApplicationElement {
  static styles = [ApplicationElement.styles, style];

  static properties = {
    startDate: { type: Date },
    endDate: { type: Date },
    allDay: { type: Boolean },
    label: { type: String },
    url: { type: String, reflect: true },
  };

  /** @internal */
  static translations = translations;

  constructor() {
    super();
    this.addEventListener('click', this);
  }

  /** @internal */
  get shouldDisplayCurrentDate() {
    const currentDate = this.resetDateTiming(new Date());

    return (
      currentDate > this.resetDateTiming(this.startDate) &&
      currentDate <= this.resetDateTiming(this.endDate)
    );
  }

  /** @internal */
  get finalDate() {
    return this.shouldDisplayCurrentDate ? new Date() : this.startDate;
  }

  resetDateTiming(date) {
    const tempDate = new Date(date);

    tempDate.setHours(0);
    tempDate.setMinutes(0);
    tempDate.setSeconds(0);
    tempDate.setMilliseconds(0);

    return tempDate;
  }

  handleEvent(e) {
    if (e.type === 'click') this.handleClick(e);
  }

  handleClick(e) {
    if (!this.url || e.target.tagName !== 'XS-CARD-V2') return;

    this.shadowRoot.querySelector('a').click();
  }

  renderLabel() {
    return html`<ef-label layout="md">${this.label}</ef-label>`;
  }

  renderSlot() {
    return html` <slot></slot>`;
  }

  renderSlotWithLink() {
    return html` <a href=${this.url}>${this.renderSlot()}</a>`;
  }

  render() {
    return html`
      <xs-card-date-widget .date=${this.finalDate} dayNameOn>
      </xs-card-date-widget>
      <section>
        <div class="main-content">
          ${this.url ? this.renderSlotWithLink() : this.renderSlot()}
          <xs-time-range
            from=${this.startDate}
            to=${this.endDate}
            timeOn=${!this.allDay}
            yearOn>
          </xs-time-range>
        </div>
        <footer>
          <slot name="footer"></slot>
          ${this.label ? this.renderLabel() : null}
        </footer>
      </section>
    `;
  }
}

Card_V2.registerTagName('xs-card-v2');

customElements.get('xs-card-v2') ||
  customElements.define('xs-card-v2', Card_V2);
